import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Printer, Download } from 'lucide-react';
import { useApp } from '@/store';
import { Lang, useT } from '@/lib/i18n';
import { Billing } from '@/data/types';
import { normalizeDeviceTypeKey } from '@/lib/device-type-label';
import { latestPartsFor, partsForVisit } from '@/lib/device-parts';
import {
  buildInvoiceLines,
  pickServicePrice,
  DISPOSAL_TYPE,
  disposalTargetContent,
  disposalTargetType,
  normalizeContent,
} from '@/lib/billing';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { fmtCurrency, fmtDate } from '@/lib/utils';
import { applyRounding, getCountry } from '@/lib/country';
import { exportPdf } from '@/lib/pdf';
import { resolveUploadUrl } from '@/lib/uploads';
import { SectionId } from '@/lib/section-permissions';
import { useSectionRights } from '@/lib/use-section-rights';
import { ResponsiveGrid, ScrollTable } from '@/components/layout';

export function Invoice() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const visitId = searchParams.get('visit');
  const customer = useApp(s => s.customers.find(c => c.id === id));
  const settings = useApp(s => s.settings);
  const servicePrices = useApp(s => s.servicePrices);
  const spareParts = useApp(s => s.spareParts);
  const types = useApp(s => s.deviceTypes);
  const manufacturers = useApp(s => s.manufacturers);
  const inspections = useApp(s => s.inspections);
  const lang = useApp(s => s.lang);
  const t = useT();
  // Invoices are generated, never stored (#95): Erstellen = print / PDF,
  // Bearbeiten = adjust line prices and VAT before printing.
  const invoiceRights = useSectionRights()[SectionId.INVOICES];

  // When invoicing a specific visit, charge only the devices serviced that
  // visit; otherwise fall back to the whole register (per-customer invoice).
  const servicedSet = useMemo(() => {
    if (!customer) return new Set<string>();
    if (visitId) {
      const insp = inspections.find(i => i.visitId === visitId);
      if (insp) return new Set(insp.devicesServiced);
    }
    return new Set<string>([
      ...customer.extinguishers.map(e => e.id),
      ...customer.hoses.map(h => h.id),
    ]);
  }, [customer, inspections, visitId]);

  // When invoicing a specific visit, surface that inspection's signature +
  // technician/date on the invoice (the proof-of-service the customer signed).
  const inspection = useMemo(
    () => (visitId ? inspections.find(i => i.visitId === visitId) : undefined),
    [visitId, inspections]
  );
  // Inspections for this customer, the source of truth for fitted parts.
  const customerInspections = useMemo(
    () => inspections.filter(i => i.customerId === id),
    [inspections, id]
  );
  const [sigUrl, setSigUrl] = useState<string | null>(null);
  useEffect(() => {
    let alive = true;
    const key = inspection?.signatureKey;
    if (!key) {
      setSigUrl(null);
      return;
    }
    resolveUploadUrl(key)
      .then(u => alive && setSigUrl(u))
      .catch(() => alive && setSigUrl(null));
    return () => {
      alive = false;
    };
  }, [inspection?.signatureKey]);
  const printRef = useRef<HTMLDivElement>(null);
  const vatInputId = useId();
  const grossToggleId = useId();
  const [priceOverrides, setPriceOverrides] = useState<Record<string, number>>(
    {}
  );
  const [vatOverride, setVatOverride] = useState<number | null>(null);
  // Toggle between net line prices (VAT added at the end) and gross line prices
  // (VAT already included) — matches the client's legacy "incl. MwSt." switch.
  const [showGross, setShowGross] = useState(false);
  const setLinePrice = (key: string, price: number) =>
    setPriceOverrides(prev => ({ ...prev, [key]: price }));

  const handlePdf = () => {
    if (printRef.current) {
      exportPdf(
        printRef.current,
        `RE-${customer?.id}-${new Date().getFullYear()}.pdf`
      );
    }
  };

  const { sections: lines, warnings: lineWarnings } = useMemo(() => {
    if (!customer) return { sections: [], warnings: [] };
    // buildInvoiceLines reports the prices it could not resolve. Dropping them
    // is what produced silent "CHF 0.00" rows on the invoice — a new device
    // whose model has no price, or a disposal with no disposal row.
    const priceWarnings: string[] = [];
    const out: {
      section: string;
      items: { articleNr: string; desc: string; qty: number; price: number }[];
    }[] = [];
    // One line per distinct part, quantity = how many devices consumed it.
    // Parts are read off the inspection, not the device: sign-off clears the
    // device field (client #86/#87), which had stopped fitted parts reaching
    // the invoice at all. A visit invoice bills what that visit consumed; the
    // whole-register invoice falls back to each device's last inspection,
    // which is what the device field used to hold.
    const partsOf = (deviceId: string) =>
      visitId
        ? partsForVisit(deviceId, inspection)
        : latestPartsFor(deviceId, customerInspections);
    const partLinesFor = (devices: { id: string }[]) => {
      const agg: Record<string, { desc: string; qty: number; price: number }> =
        {};
      devices.forEach(d => {
        partsOf(d.id).forEach(a => {
          const part = spareParts.find(p => p.articleNr === a);
          if (!part) return;
          if (!agg[part.articleNr])
            agg[part.articleNr] = {
              desc: part.name,
              qty: 0,
              price: part.price,
            };
          agg[part.articleNr].qty += 1;
        });
      });
      return Object.entries(agg).map(([articleNr, v]) => ({
        articleNr,
        desc: v.desc,
        qty: v.qty,
        price: v.price,
      }));
    };
    const grouped: Record<string, typeof customer.extinguishers> = {};
    customer.extinguishers.forEach(e => {
      // When invoicing a specific visit, charge only serviced + MAINTENANCE
      // units (NONE never charged; NEW/DISPOSED summarised below). The
      // whole-register per-customer invoice keeps every device unchanged.
      if (visitId) {
        if (!servicedSet.has(e.id)) return;
        if ((e.billing ?? Billing.MAINTENANCE) !== Billing.MAINTENANCE) return;
      }
      // Group on the canonical device-type id: the same type reaches us as a
      // label on imported devices and as an id on app-written ones, which
      // otherwise bills as two identical "Wartung Schaum Aufladedruck" lines
      // on one invoice (client #65 / #80).
      const k = e.type ? normalizeDeviceTypeKey(e.type, types) : 'misc';
      grouped[k] = grouped[k] ?? [];
      grouped[k].push(e);
    });
    // The volume discount is earned by the visit, not by the device type: a
    // site with 1 CO2 and 3 foam units is a 4-extinguisher job and every unit
    // is priced at the 2-5 tier (client #55, #56).
    const totalMaintained = Object.values(grouped).reduce(
      (n, items) => n + items.length,
      0
    );
    // The per-type headings ("Schaum Aufladedruck", "Co2 Feuerlöscher", …) are
    // gone: the client bills from one continuous list of positions and does not
    // want the breakdown (client #65). The type still names each maintenance
    // line, so nothing is lost — only the section bands. New/disposal and hose
    // lines keep their headings; those are categories, not extinguisher types.
    const extinguisherItems: {
      articleNr: string;
      desc: string;
      qty: number;
      price: number;
    }[] = [];
    Object.entries(grouped).forEach(([type, items]) => {
      const tRow = types.find(x => x.id === type);
      const tName = tRow
        ? ((lang === Lang.DE ? tRow.nameDe : tRow.nameEn) ?? tRow.nameDe)
        : type;
      const priceRow = pickServicePrice(servicePrices, type, totalMaintained);
      const price = priceRow?.price ?? 0;
      const serviceLine = {
        articleNr: priceRow?.articleNr ?? '—',
        desc: `${t('invoice.maintenance')} ${tName}`,
        qty: items.length,
        price,
      };
      extinguisherItems.push(serviceLine, ...partLinesFor(items));
    });
    if (extinguisherItems.length)
      out.push({ section: '', items: extinguisherItems });
    // New + disposal lines (serviced devices, by billing type). Devices store
    // the model NAME, so index the catalogue the same way; disposal is priced
    // from a Service Prices row on the reserved `disposal` device type.
    const modelPrices: Record<string, number> = {};
    manufacturers.forEach(m =>
      m.models.forEach(mo => {
        if (mo.price != null) modelPrices[mo.name] = mo.price;
      })
    );
    const typeName = (id?: string) => {
      if (!id) return '';
      const row = types.find(x => x.id === id);
      return row
        ? ((lang === Lang.DE ? row.nameDe : row.nameEn) ?? row.nameDe)
        : id;
    };
    const disposalRow = servicePrices.find(p => p.deviceType === DISPOSAL_TYPE);
    // Disposal fees differ per device (client #54). The client's list is by
    // size, which only the device's content ("6 kg Pulver") records, so a
    // content row wins over a device-type row; the generic row above still
    // covers anything without its own.
    const disposalByType: Record<string, { articleNr: string; price: number }> =
      {};
    const disposalByContent: Record<
      string,
      { articleNr: string; price: number; label: string }
    > = {};
    servicePrices.forEach(p => {
      const target = disposalTargetType(p.deviceType);
      if (target)
        disposalByType[target] = { articleNr: p.articleNr, price: p.price };
      const content = disposalTargetContent(p.deviceType);
      const key = content ? normalizeContent(content) : '';
      if (content && key)
        disposalByContent[key] = {
          articleNr: p.articleNr,
          price: p.price,
          label: content,
        };
    });
    const built = buildInvoiceLines({
      devices: customer.extinguishers.map(e => ({
        id: e.id,
        billing: e.billing,
        model: e.model,
        type: e.type ? normalizeDeviceTypeKey(e.type, types) : undefined,
        content: e.content,
      })),
      servicedIds: [...servicedSet],
      servicePrices,
      serviceArticleNr: '',
      modelPrices,
      disposalPrice: disposalRow?.price,
      disposalArticleNr: disposalRow?.articleNr,
      disposalByType,
      disposalByContent,
    });
    priceWarnings.push(...built.warnings);
    const extra = built.lines.filter(l => l.kind !== 'service');
    if (visitId && extra.length) {
      out.push({
        section: t('invoice.newAndDisposal'),
        items: extra.map(l => ({
          articleNr: l.articleNr,
          desc:
            l.kind === 'new'
              ? `${t('invoice.totalNew')} ${l.label.replace(/^Total Neu /, '')}`
              : [t('invoice.disposal'), l.content ?? typeName(l.deviceType)]
                  .filter(Boolean)
                  .join(' · '),
          qty: l.qty,
          price: l.price,
        })),
      });
    }
    // Hose maintenance section (serviced hoses only).
    const servicedHoses = customer.hoses.filter(h => servicedSet.has(h.id));
    if (servicedHoses.length) {
      const priceRow = pickServicePrice(
        servicePrices,
        'loeschposten',
        servicedHoses.length
      );
      const price = priceRow?.price ?? 0;
      out.push({
        section: t('invoice.fireHoses'),
        items: [
          {
            articleNr: priceRow?.articleNr ?? '—',
            desc: t('invoice.hoseMaintenance'),
            qty: servicedHoses.length,
            price,
          },
          // Parts fitted to hoses are billed like extinguisher parts (#17).
          ...partLinesFor(servicedHoses),
        ],
      });
    }
    return { sections: out, warnings: priceWarnings };
  }, [
    customer,
    servicePrices,
    spareParts,
    types,
    manufacturers,
    lang,
    servicedSet,
    visitId,
    inspection,
    customerInspections,
    t,
  ]);

  const billingWarnings = useMemo(() => {
    // Unresolved prices are worth flagging on any invoice, not only one opened
    // from a visit — a zero-priced row is wrong however the page was reached.
    if (!customer) return lineWarnings;
    const w: string[] = [...lineWarnings];
    if (!visitId) return w;
    const servicedTypes = new Set(
      customer.extinguishers
        .filter(
          e =>
            servicedSet.has(e.id) &&
            (e.billing ?? Billing.MAINTENANCE) === Billing.MAINTENANCE
        )
        .map(e => e.type ?? 'misc')
    );
    servicedTypes.forEach(type => {
      if (!servicePrices.find(p => p.deviceType === type)) {
        const tRow = types.find(x => x.id === type);
        w.push(
          `${t('invoice.missingPrice')}: ${tRow ? (tRow.nameEn ?? tRow.nameDe) : type}`
        );
      }
    });
    return w;
  }, [customer, servicePrices, types, servicedSet, visitId, lineWarnings, t]);

  if (!customer) return <div>{t('common.notFound')}</div>;

  const country = getCountry(settings.country);

  const linesTotal = lines
    .flatMap((l, si) =>
      l.items.map((i, ii) => (priceOverrides[`${si}-${ii}`] ?? i.price) * i.qty)
    )
    .reduce((s, v) => s + v, 0);
  const subtotal = linesTotal + settings.travelSurcharge;
  const effectiveVat = vatOverride ?? settings.vatPercent;
  const grossFactor = 1 + effectiveVat / 100;
  const vat = subtotal * (effectiveVat / 100);
  const totalBeforeRound = subtotal + vat;
  const total = applyRounding(totalBeforeRound, settings.rounding);
  const roundingDiff = total - totalBeforeRound;

  const todayIso = new Date().toISOString().slice(0, 10);
  const dueIso = new Date(Date.now() + settings.paymentTerms * 86400000)
    .toISOString()
    .slice(0, 10);

  return (
    <div className="space-y-4">
      {billingWarnings.length > 0 && (
        <div className="no-print rounded-md border border-amber-500 bg-amber-50 p-3 text-sm text-amber-800">
          {billingWarnings.map((w, i) => (
            <div key={i}>⚠ {w}</div>
          ))}
        </div>
      )}
      <div className="no-print flex justify-between items-center">
        <Link
          to={`/customer/${customer.id}`}
          className="text-sm text-muted-foreground hover:text-primary inline-flex items-center gap-1"
        >
          <ArrowLeft className="h-3 w-3" /> {t('common.back')}
        </Link>
        {invoiceRights.create && (
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => window.print()}>
              <Printer className="h-4 w-4" />
              {t('common.print')}
            </Button>
            <Button size="sm" onClick={handlePdf}>
              <Download className="h-4 w-4" />
              {t('common.pdf')}
            </Button>
          </div>
        )}
      </div>

      <Card
        ref={printRef}
        className="print-page max-w-[210mm] mx-auto p-4 sm:p-8"
      >
        <div className="border-b pb-4 mb-6 grid gap-4 sm:grid-cols-2">
          <div>
            <div className="font-bold text-lg text-primary">
              {settings.companyName}
            </div>
            <div className="text-xs">{settings.street}</div>
            <div className="text-xs">
              {settings.zip} {settings.city}
            </div>
            <div className="text-xs">
              {settings.phone} · {settings.email}
            </div>
            <div className="text-xs mt-1">
              {country.vatNumberLabel}: {settings.vatNumber}
            </div>
          </div>
          <div className="text-right">
            <div className="text-xl font-bold">{t('invoice.title')}</div>
            <div className="text-xs mt-2">
              #{' '}
              <span className="font-mono">
                RE-{customer.id}-{new Date().getFullYear()}
              </span>
            </div>
            <div className="text-xs">
              {t('invoice.date')}: {fmtDate(todayIso)}
            </div>
            <div className="text-xs">
              {t('invoice.due')}: {fmtDate(dueIso)}
            </div>
          </div>
        </div>

        <ResponsiveGrid cols={3} className="mb-6">
          <div className="text-sm">
            <div className="font-semibold mb-1">{t('invoice.billTo')}</div>
            <div className="text-xs leading-relaxed">
              {customer.billingAddress?.company}
              <br />
              {customer.billingAddress?.attn && (
                <>
                  {customer.billingAddress.attn}
                  <br />
                </>
              )}
              {customer.billingAddress?.street}
              <br />
              {customer.billingAddress?.zip} {customer.billingAddress?.city}
            </div>
          </div>
          <div className="text-sm">
            <div className="font-semibold mb-1">{t('invoice.object')}</div>
            <div className="text-xs leading-relaxed">
              {customer.objectAddress?.company}
              <br />
              {customer.objectAddress?.street}
              <br />
              {customer.objectAddress?.zip} {customer.objectAddress?.city}
            </div>
          </div>
          <div className="text-sm">
            <div className="font-semibold mb-1">{t('invoice.owner')}</div>
            <div className="text-xs leading-relaxed">
              {customer.ownerAddress?.company ?? '—'}
              <br />
              {customer.ownerAddress?.street}
              <br />
              {customer.ownerAddress?.zip} {customer.ownerAddress?.city}
            </div>
          </div>
        </ResponsiveGrid>

        <ScrollTable minWidth={500}>
          <table className="w-full text-xs mb-4">
            <thead className="border-b">
              <tr className="text-left">
                <th className="py-2 w-10">#</th>
                <th>{t('invoice.article')}</th>
                <th>{t('invoice.description')}</th>
                <th className="text-right w-16">{t('invoice.qty')}</th>
                <th className="w-12"></th>
                <th className="text-right w-24" data-focus="invoice-price">
                  {t('invoice.price')}
                </th>
                <th className="text-right w-24">{t('invoice.total')}</th>
              </tr>
            </thead>
            <tbody>
              {lines.flatMap((sec, si) => [
                // A blank section renders no heading band — see #65.
                ...(sec.section
                  ? [
                      <tr key={`s-${si}`} className="bg-primary/5">
                        <td colSpan={7} className="py-1 px-2 font-semibold">
                          {sec.section}
                        </td>
                      </tr>,
                    ]
                  : []),
                ...sec.items.map((it, ii) => {
                  const key = `${si}-${ii}`;
                  const effectivePrice = priceOverrides[key] ?? it.price;
                  return (
                    <tr
                      key={key}
                      data-testid="invoice-line-row"
                      className="border-b"
                    >
                      <td className="py-1">{ii + 1}</td>
                      <td className="font-mono">{it.articleNr}</td>
                      <td>{it.desc}</td>
                      <td className="text-right">{it.qty}</td>
                      <td className="text-muted-foreground">
                        {t('invoice.unit')}
                      </td>
                      <td className="text-right no-print">
                        <Input
                          type="number"
                          min="0"
                          step="0.01"
                          disabled={!invoiceRights.edit}
                          value={effectivePrice}
                          onChange={e =>
                            setLinePrice(key, parseFloat(e.target.value) || 0)
                          }
                          className="w-16 sm:w-24 h-7 text-right text-xs"
                        />
                      </td>
                      <td className="text-right">
                        {fmtCurrency(
                          (showGross
                            ? effectivePrice * grossFactor
                            : effectivePrice) * it.qty,
                          settings.currency
                        )}
                      </td>
                    </tr>
                  );
                }),
              ])}
              {settings.travelSurcharge > 0 && (
                <tr className="border-b">
                  <td className="py-1"></td>
                  <td className="font-mono">—</td>
                  <td>{t('invoice.travelSurcharge')}</td>
                  <td className="text-right">1</td>
                  <td className="text-muted-foreground">{t('invoice.unit')}</td>
                  <td className="text-right">
                    {fmtCurrency(settings.travelSurcharge, settings.currency)}
                  </td>
                  <td className="text-right">
                    {fmtCurrency(
                      showGross
                        ? settings.travelSurcharge * grossFactor
                        : settings.travelSurcharge,
                      settings.currency
                    )}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </ScrollTable>

        <div className="flex justify-end">
          <div className="w-full sm:w-72 text-sm space-y-1">
            <div className="no-print flex items-center justify-between gap-2">
              <Label htmlFor={vatInputId} className="text-muted-foreground">
                {t('invoice.vatOverride')}
              </Label>
              <Input
                id={vatInputId}
                data-testid="invoice-vat-input"
                disabled={!invoiceRights.edit}
                type="number"
                min="0"
                step="0.1"
                value={effectiveVat}
                onChange={e => {
                  const raw = e.target.value;
                  if (raw === '') {
                    setVatOverride(null);
                    return;
                  }
                  const parsed = parseFloat(raw);
                  setVatOverride(Number.isFinite(parsed) ? parsed : null);
                }}
                className="w-20 h-7 text-right text-xs"
              />
            </div>
            <div className="no-print flex items-center gap-2 pb-1">
              <Checkbox
                id={grossToggleId}
                data-testid="invoice-gross-toggle"
                checked={showGross}
                onCheckedChange={v => setShowGross(v === true)}
              />
              <Label
                htmlFor={grossToggleId}
                className="text-muted-foreground cursor-pointer"
              >
                {t('invoice.showGross')}
              </Label>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">
                {t('invoice.totalExcl')}
              </span>
              <span>{fmtCurrency(subtotal, settings.currency)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">
                {showGross ? t('invoice.inclVat') : country.taxLabel}{' '}
                {effectiveVat}%
              </span>
              <span>{fmtCurrency(vat, settings.currency)}</span>
            </div>
            {Math.abs(roundingDiff) >= 0.005 && (
              <div
                data-testid="invoice-rounding-row"
                className="flex justify-between"
              >
                <span className="text-muted-foreground">
                  {t('invoice.rounding')}
                </span>
                <span data-testid="invoice-rounding-value">
                  {fmtCurrency(roundingDiff, settings.currency)}
                </span>
              </div>
            )}
            <div
              data-testid="invoice-total-row"
              className="flex justify-between border-t pt-1 font-bold text-base"
            >
              <span>{t('invoice.totalIncl')}</span>
              <span data-testid="invoice-total-value">
                {fmtCurrency(total, settings.currency)}
              </span>
            </div>
          </div>
        </div>

        <div className="mt-8 text-xs text-muted-foreground space-y-1">
          <div>
            {t('invoice.paymentTerms')}: {settings.paymentTerms}{' '}
            {t('invoice.daysNet')}
          </div>
          {settings.iban && <div>IBAN: {settings.iban}</div>}
          <div className="pt-4">{t('invoice.thanks')}</div>
        </div>

        {inspection?.signatureKey && (
          <div className="mt-8 pt-4 border-t">
            <div className="text-xs text-muted-foreground mb-1">
              {t('invoice.signature')}
            </div>
            {sigUrl && (
              <img src={sigUrl} alt="" className="h-16 w-auto object-contain" />
            )}
            <div className="text-xs text-muted-foreground mt-1 border-t pt-1 inline-block">
              {inspection.technician} · {fmtDate(inspection.date)}
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}
